import { useEffect, useRef, useState } from 'react';
import { featureUtil } from 'utils/geoJSONFeatureUtils/featureUtil';
import { v4 as uuidv4 } from 'uuid';

import { centroid, circle as circleGeometry, destination, distance } from '@turf/turf';

import useMapContext from 'components/Map/hooks/useMapContext';
import useMapEvents from 'components/Map/hooks/useMapEvents';

import {
    CIRCLE_MIN_RADIUS_METERS,
    CIRCLE_OPTIONS,
    CIRCLE_STEP_OPTIONS,
    CIRCLE_UNITS,
    DRAWING_LAYERS_BY_MODE,
    DRAWING_MODE_TO_GEOJSON_BOUNDARY_TYPE_MAP,
    GEOJSON_POLYGON_TYPE,
    GEOJSON_PROPERTY,
    MAP_ACTION_TYPE,
    MAP_EVENT_TYPE
} from './constants';
import type {
    DrawingMode,
    DrawingTool,
    Feature,
    InitializeCenterPointArgs,
    LineString,
    MapAction,
    MapVertexChangedEvent,
    Point,
    Polygon,
    Position,
    Props
} from './types';

const useCircle = ({
    onCompleteFeature,
    onUpdateCirclePoints,
    onUpdateDrawing
}: Props): DrawingTool => {
    const { subscribe, unsubscribe } = useMapEvents();
    const centerPointRef = useRef<Feature<Point> | undefined>(undefined);
    const perimeterPointRef = useRef<Feature<Point> | undefined>(undefined);

    const radiusRef = useRef<number>(0);

    const centerLineStringFeatureRef = useRef<Feature<LineString> | undefined>(undefined);
    const circleOutlineRef = useRef<Feature<LineString> | undefined>(undefined);
    const circleFeatureRef = useRef<Feature<Polygon> | undefined>(undefined);
    const selectedPointFeatureRef = useRef<Feature<Point> | undefined>(undefined);

    const { createLineStringFeature, createPointFeature, hasFeatureWithId, isCircle } = featureUtil;

    const { features, mapState } = useMapContext();
    const { addFeature, removeFeature, updateFeature } = features;
    const { drawingMode, selectedFeature, setIsDrawing, setIsPointSelected } = mapState;

    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    useEffect(() => {
        const handleVertexChanged = (event: MapVertexChangedEvent) => {
            const { vertexPosition } = event.detail;

            updateNextPoint(vertexPosition);
        };

        subscribe(MAP_EVENT_TYPE.MAP_VERTEX_CHANGED, handleVertexChanged);

        return () => {
            unsubscribe(MAP_EVENT_TYPE.MAP_VERTEX_CHANGED, handleVertexChanged);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedFeature) {
            setIsLoaded(false);
        }
    }, [selectedFeature]);

    const getLayersByMode = () => {
        if (!centerPointRef.current) {
            return DRAWING_LAYERS_BY_MODE[drawingMode];
        }

        const boundaryDrawingMode = centerPointRef.current.properties?.[
            GEOJSON_PROPERTY.BOUNDARY_TYPE
        ] as DrawingMode;

        return DRAWING_LAYERS_BY_MODE[boundaryDrawingMode];
    };

    const initializeDrawing = (initialPoint: Position) => {
        resetDrawing();

        setIsDrawing(true);
        initializeCenterPoint({ id: uuidv4(), point: initialPoint });
        initializePerimeterPoint(initialPoint, uuidv4());
        initializeCenterLineString([initialPoint, initialPoint], uuidv4());
        initializeCircleFeature(initialPoint, 0, uuidv4());
        initializeCircleOutlineStringFeature(initialPoint, 0, uuidv4());

        radiusRef.current = 0;
        selectedPointFeatureRef.current = perimeterPointRef.current;
        setIsPointSelected(true);
    };

    const initializeCenterPoint = ({
        drawingModeOverride,
        id,
        point
    }: InitializeCenterPointArgs) => {
        const centerPointFeature = createPointFeature(point, id);

        centerPointFeature.properties = {
            ...centerPointFeature.properties,
            [GEOJSON_PROPERTY.BOUNDARY_TYPE]:
                drawingModeOverride ?? DRAWING_MODE_TO_GEOJSON_BOUNDARY_TYPE_MAP[drawingMode],
            [GEOJSON_PROPERTY.IS_DRAWING]: true,
            [GEOJSON_PROPERTY.IS_SELECTED]: true
        };

        const { vertexLayer } = getLayersByMode();

        addFeature({
            feature: centerPointFeature,
            layerName: vertexLayer
        });

        centerPointRef.current = centerPointFeature;
    };

    const initializePerimeterPoint = (point: Position, id: string) => {
        const perimeterPointFeature = createPointFeature(point, id);

        perimeterPointRef.current = perimeterPointFeature;

        const { vertexLayer } = getLayersByMode();

        addFeature({
            feature: perimeterPointFeature,
            layerName: vertexLayer
        });
    };

    const initializeCenterLineString = (points: Position[], id: string) => {
        const centerLineStringFeature = createLineStringFeature(points, id);

        centerLineStringFeatureRef.current = centerLineStringFeature;

        const { centerRadiusLineLayer } = getLayersByMode();

        addFeature({
            feature: centerLineStringFeature,
            layerName: centerRadiusLineLayer
        });
    };

    const initializeCircleFeature = (point: Position, radiusMeters: number, id: string) => {
        if (!centerPointRef.current) {
            return;
        }

        const newCircleFeature = circleGeometry(point, radiusMeters, CIRCLE_OPTIONS);

        circleFeatureRef.current = newCircleFeature;
        circleFeatureRef.current.id = id;

        const boundaryType = centerPointRef.current.properties?.[GEOJSON_PROPERTY.BOUNDARY_TYPE];

        circleFeatureRef.current.properties = {
            ...circleFeatureRef.current.properties,
            [GEOJSON_PROPERTY.CENTER_POINT]: point,
            [GEOJSON_PROPERTY.RADIUS_METERS]: radiusMeters,
            [GEOJSON_PROPERTY.BOUNDARY_TYPE]: boundaryType,
            [GEOJSON_PROPERTY.IS_SELECTED]: true
        };

        const { circleFillLayer } = getLayersByMode();

        addFeature({ feature: newCircleFeature, layerName: circleFillLayer });
    };

    const initializeCircleOutlineStringFeature = (
        point: Position,
        radiusMeters: number,
        id: string
    ) => {
        const circleOutlineStringFeature = circleGeometry(point, radiusMeters, CIRCLE_OPTIONS);

        circleOutlineRef.current = createLineStringFeature(
            circleOutlineStringFeature.geometry.coordinates[0],
            id
        );

        const { circleOutlineLayer } = getLayersByMode();

        addFeature({
            feature: circleOutlineRef.current,
            layerName: circleOutlineLayer
        });
    };

    const handleMapAction = (action: MapAction) => {
        if (action.type !== MAP_ACTION_TYPE.CIRCLE_UPDATE_POINTS) {
            return;
        }

        if (!action.circle.id || !isCircle(action.circle)) {
            return;
        }

        loadDrawing(action.circle);
        setIsDrawing(true);
    };

    const loadDrawing = (drawing: Feature<Polygon>) => {
        if (!drawing.id || !isCircle(drawing)) {
            return;
        }

        resetDrawing();

        const circlePoints = drawing.geometry.coordinates[0];
        const centerPoint =
            drawing.properties?.[GEOJSON_PROPERTY.CENTER_POINT] ||
            centroid(drawing).geometry.coordinates;
        const radiusMeters =
            drawing.properties?.[GEOJSON_PROPERTY.RADIUS_METERS] ||
            distance(centerPoint, circlePoints[0], CIRCLE_OPTIONS);
        const northPointFromCenter = destination(centerPoint, radiusMeters, 0, CIRCLE_OPTIONS);

        initializeCenterPoint({
            drawingModeOverride: drawing.properties?.[
                GEOJSON_PROPERTY.BOUNDARY_TYPE
            ] as DrawingMode,
            id: uuidv4(),
            point: centerPoint
        });

        initializePerimeterPoint(northPointFromCenter.geometry.coordinates, uuidv4());

        initializeCenterLineString(
            [centerPoint, northPointFromCenter.geometry.coordinates],
            uuidv4()
        );
        initializeCircleOutlineStringFeature(centerPoint, radiusMeters, uuidv4());
        initializeCircleFeature(centerPoint, radiusMeters, drawing.id as string);

        if (!isLoaded) {
            onUpdateCirclePoints?.(drawing);
        }

        onUpdateDrawing?.(circleFeatureRef.current as Feature<Polygon>);
        setIsLoaded(true);
    };

    const updateNextPoint = (nextPosition: Position) => {
        if (!selectedPointFeatureRef.current || !centerPointRef.current) {
            return;
        }

        const isCenterPointSelected = selectedPointFeatureRef.current === centerPointRef.current;

        if (isCenterPointSelected) {
            updateCenterPoint(nextPosition);
            updatePerimeterPoint();
        } else {
            updatePerimeterPoint(nextPosition);
        }

        updateRadiusLine();
        updateCirclePolygon();

        if (circleFeatureRef.current) {
            onUpdateDrawing?.(circleFeatureRef.current);
        }
    };

    const updateCenterPoint = (nextPosition: Position) => {
        if (!centerPointRef.current || selectedPointFeatureRef.current !== centerPointRef.current) {
            return;
        }

        centerPointRef.current.geometry.coordinates = nextPosition;
        updateFeature(centerPointRef.current);
    };

    const updatePerimeterPoint = (nextPosition?: Position) => {
        if (!perimeterPointRef.current || !centerPointRef.current) {
            return;
        }

        const centerPoint = centerPointRef.current.geometry.coordinates;

        if (nextPosition) {
            perimeterPointRef.current.geometry.coordinates = nextPosition;
            radiusRef.current = distance(centerPoint, nextPosition, CIRCLE_OPTIONS);
        } else {
            const northPointFromCenter = destination(
                centerPoint,
                radiusRef.current,
                0,
                CIRCLE_OPTIONS
            );

            perimeterPointRef.current.geometry.coordinates =
                northPointFromCenter.geometry.coordinates;
        }

        updateFeature(perimeterPointRef.current);
    };

    const updateRadiusLine = () => {
        if (
            !centerLineStringFeatureRef.current ||
            !centerPointRef.current ||
            !perimeterPointRef.current
        ) {
            return;
        }

        centerLineStringFeatureRef.current.geometry.coordinates = [
            centerPointRef.current?.geometry.coordinates,
            perimeterPointRef.current?.geometry.coordinates
        ];

        updateFeature(centerLineStringFeatureRef.current);
    };

    const updateCirclePolygon = () => {
        if (
            !centerPointRef.current ||
            !perimeterPointRef.current ||
            !circleFeatureRef.current ||
            !circleOutlineRef.current
        ) {
            return;
        }

        const centerPointPosition = centerPointRef.current.geometry.coordinates;
        const perimeterPointPosition = perimeterPointRef.current.geometry.coordinates;
        const radiusMeters = distance(centerPointPosition, perimeterPointPosition, {
            units: CIRCLE_UNITS
        });
        const newPolygonFeature = circleGeometry(
            centerPointPosition,
            radiusMeters,
            CIRCLE_STEP_OPTIONS
        );

        circleFeatureRef.current.geometry.coordinates = newPolygonFeature.geometry.coordinates;
        updateFeature(circleFeatureRef.current);

        circleOutlineRef.current.geometry.coordinates = newPolygonFeature.geometry.coordinates[0];
        updateFeature(circleOutlineRef.current);
    };

    const handleClick = (point: Position, featuresUnderPoint?: Feature[]) => {
        const isInitialized = centerPointRef.current;

        if (!isInitialized) {
            initializeDrawing(point);

            return;
        }

        if (selectedPointFeatureRef.current) {
            setIsPointSelected(false);
            completeDrawing();

            return;
        }

        const selectedPerimeterPoint = hasFeatureWithId(
            featuresUnderPoint,
            perimeterPointRef.current?.id as string
        );

        if (selectedPerimeterPoint) {
            selectedPointFeatureRef.current = perimeterPointRef.current;
            setIsPointSelected(true);
            setIsDrawing(true);

            return;
        }

        const selectedCenterPoint = hasFeatureWithId(
            featuresUnderPoint,
            centerPointRef.current?.id as string
        );

        if (selectedCenterPoint) {
            selectedPointFeatureRef.current = centerPointRef.current;
            setIsPointSelected(true);
            setIsDrawing(true);

            return;
        }
    };

    const completeDrawing = () => {
        if (
            !centerPointRef.current ||
            !perimeterPointRef.current ||
            !circleFeatureRef.current ||
            !centerLineStringFeatureRef.current ||
            !circleOutlineRef.current
        ) {
            return;
        }

        const centerPointPosition = centerPointRef.current.geometry.coordinates;
        const perimeterPointPosition = perimeterPointRef.current.geometry.coordinates;

        const finalCircle = makeFinalCircle(centerPointPosition, perimeterPointPosition);

        if (!finalCircle) {
            return;
        }

        removeFeature(centerPointRef.current.id as string);
        removeFeature(perimeterPointRef.current.id as string);
        removeFeature(centerLineStringFeatureRef.current?.id as string);
        removeFeature(circleOutlineRef.current?.id as string);

        resetInternalState();

        onCompleteFeature(finalCircle);

        onUpdateCirclePoints?.(finalCircle);

        selectedPointFeatureRef.current = undefined;
    };

    const makeFinalCircle = (centerPointPosition: Position, perimeterPointPosition: Position) => {
        if (
            !circleFeatureRef.current ||
            !centerPointRef.current ||
            !perimeterPointRef.current ||
            !centerLineStringFeatureRef.current ||
            !circleOutlineRef.current
        ) {
            return;
        }

        const radiusMeters = distance(centerPointPosition, perimeterPointPosition, CIRCLE_OPTIONS);

        if (radiusMeters < CIRCLE_MIN_RADIUS_METERS) {
            return;
        }

        const finalCircle = circleGeometry(centerPointPosition, radiusMeters, CIRCLE_STEP_OPTIONS);

        finalCircle.id = circleFeatureRef.current.id;

        finalCircle.properties = {
            ...circleFeatureRef.current.properties,
            [GEOJSON_PROPERTY.CENTER_POINT]: centerPointPosition,
            [GEOJSON_PROPERTY.RADIUS_METERS]: radiusMeters,
            [GEOJSON_PROPERTY.IS_SELECTED]: true
        };

        finalCircle.properties[GEOJSON_PROPERTY.POLYGON_TYPE] = GEOJSON_POLYGON_TYPE.CIRCLE;

        return finalCircle;
    };

    const resetInternalState = () => {
        centerPointRef.current = undefined;
        perimeterPointRef.current = undefined;
        centerLineStringFeatureRef.current = undefined;
        circleFeatureRef.current = undefined;
        circleOutlineRef.current = undefined;
        selectedPointFeatureRef.current = undefined;
    };

    const resetDrawing = () => {
        const featuresToRemove = [
            centerPointRef.current,
            perimeterPointRef.current,
            centerLineStringFeatureRef.current,
            circleFeatureRef.current,
            circleOutlineRef.current
        ];

        featuresToRemove.forEach(feature => {
            if (feature?.id) {
                removeFeature(feature.id as string);
            }
        });

        resetInternalState();
        setIsDrawing(false);
    };

    return {
        completeDrawing,
        handleClick,
        handleMapAction,
        isDrawing: centerPointRef.current !== undefined,
        loadDrawing,
        resetDrawing,
        updateNextPoint
    };
};

export default useCircle;
